import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { PageShell } from "../components/layout/PageShell";
import { Breadcrumb } from "../components/Breadcrumb";
import { validateMaxLength, validateRequired } from "../../utils/validators";
import { Modal } from "../components/Modal";
import {
  ArrowLeft,
  ArrowRight,
  Search,
  Calendar,
  Check,
  ChevronRight,
  Loader2,
  User,
  Shield,
  Server,
  Wrench,
  Plus,
  Mail,
  Phone,
  ListChecks,
  X,
  Pencil,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { WorkflowStepper } from "../components/WorkflowStepper";
import { DataTable, type Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  listSSPs,
  getSSP,
  createAssessmentPlan,
  createAssessmentPlanFull,
  saveReviewedControls,
  saveAssessmentSchedule,
  submitAssessmentPlan,
  getAssessmentPlan,
  listUsers,
  listBaselineControls,
  type SspSummaryResponse,
  type SspResponse,
  type ApiUser,
  type ByComponentResponse,
  type BaselineControlPart,
  type BaselineControlResponse,
} from "../../api/frameworksApi";
import { ASSESSMENT_PLAN_CONTROLS } from "../data/assessmentPlanControlsMocks";

/* ── Types ── */

type AssessmentMethodType = "interview" | "examine" | "test";

interface LocalSSP {
  id: string;
  name: string;
  title: string;
  profileName: string;
  description: string;
  controlCount: number;
  owningOrgUnitId?: string;
}

/* ApiUser is imported from frameworksApi */

interface WizardControl {
  id: string;
  controlId: string;
  title: string;
  description: string;
  family: string;
  implementationStatus: string;
  origination: string | null;
  setParameters: { "param-id": string; values: string[] }[];
  byComponents: ByComponentResponse[];
  statements: WizardStatement[];
}

interface WizardStatement {
  id: string;
  label: string;
  text: string;
  methods: Set<AssessmentMethodType>;
}

type WizardStatementSeed = Omit<WizardStatement, "methods">;

/* ── Select-all checkbox with indeterminate support ── */
function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  return (
    <input
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-3.5 h-3.5 rounded border-[#cbd5e1] dark:border-white/20 accent-indigo-600 cursor-pointer"
    />
  );
}

/* ── Control Family Mapping ── */

const CONTROL_FAMILY_MAP: Record<string, string> = {
  AC: "Access Control",
  AT: "Awareness and Training",
  AU: "Audit and Accountability",
  CA: "Assessment, Authorization, and Monitoring",
  CM: "Configuration Management",
  CP: "Contingency Planning",
  IA: "Identification and Authentication",
  IR: "Incident Response",
  MA: "Maintenance",
  MP: "Media Protection",
  PE: "Physical and Environmental Protection",
  PL: "Planning",
  PM: "Program Management",
  PS: "Personnel Security",
  PT: "PII Processing and Transparency",
  RA: "Risk Assessment",
  SA: "System and Services Acquisition",
  SC: "System and Communications Protection",
  SI: "System and Information Integrity",
  SR: "Supply Chain Risk Management",
  IAM: "Identification and Authentication",
  CMP: "Configuration Management",
  NET: "System and Communications Protection",
  WEB: "System and Communications Protection",
  IMG: "Configuration Management",
};

function getControlFamily(controlId: string): string {
  const prefix = controlId.split("-")[0]?.toUpperCase();
  return CONTROL_FAMILY_MAP[prefix] || prefix || "Other";
}

function extractStatementSeedsFromParts(
  parts?: BaselineControlPart[],
): WizardStatementSeed[] {
  const statements: WizardStatementSeed[] = [];
  const seen = new Set<string>();

  const visit = (items?: BaselineControlPart[]) => {
    const sortedItems = [...(items ?? [])].sort(
      (a, b) =>
        (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.sortOrder ?? Number.MAX_SAFE_INTEGER),
    );

    for (const part of sortedItems) {
      const pid = part.partId || (part as any).part_id;
      const resolvedProse = (part as any).resolved_prose;
      if (part.name === "statement" && pid && !seen.has(pid)) {
        seen.add(pid);
        statements.push({
          id: part.id ?? pid,
          label: pid,
          text: resolvedProse || part.prose || "-",
        });
      }
      if (part.parts?.length) visit(part.parts);
    }
  };

  visit(parts);
  return statements;
}

/* Helper: get initials from display name */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function apiSspToLocalSSP(
  s: SspSummaryResponse,
  detail?: SspResponse,
): LocalSSP {
  const sc = detail?.systemCharacteristics;
  return {
    id: s.id,
    name: sc?.systemName || s.title || s.id,
    title: s.title || s.id,
    profileName: s.profileName || "",
    description: sc?.description || "System Security Plan",
    controlCount: detail?.systemImplementation?.components?.length ?? 0,
    owningOrgUnitId: s.owningOrgUnitId,
  };
}

/* ── Wizard Steps ── */

const WIZARD_STEPS = [
  { id: "ssp", label: "Select SSP & Define Assessment" },
  { id: "controls", label: "Select & Configure Controls" },
  { id: "assignment", label: "Assign Owners & Schedule" },
  { id: "review", label: "Review" },
];

/* ── Component ── */

export function AssessmentPlanWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editPlanId = searchParams.get("planId");
  const orgId = searchParams.get("orgId");

  const [step, setStep] = useState(0);
  const [stepping, setStepping] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [draftLoading, setDraftLoading] = useState(!!editPlanId);
  const [draftPlanId, setDraftPlanId] = useState<string | null>(editPlanId);
  const draftPlanIdRef = useRef<string | null>(editPlanId);

  /* ── Step 0: Select SSP ── */
  const [planName, setPlanName] = useState("");
  const [planNameError, setPlanNameError] = useState<string>("");
  const [apiSSPs, setApiSSPs] = useState<LocalSSP[]>([]);
  const [sspsLoading, setSSPsLoading] = useState(true);
  const [selectedSSP, setSelectedSSP] = useState<string | null>(null);

  /* ── Step 1: Select Controls ── */
  const [controls, setControls] = useState<WizardControl[]>([]);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(
    new Set(),
  );
  const [, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [expandedControls, setExpandedControls] = useState<Set<string>>(
    new Set(),
  );
  const [controlSearch, setControlSearch] = useState("");
  const [controlPage, setControlPage] = useState(0);
  const [controlPageSize, setControlPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [modalMethod, setModalMethod] = useState("");
  const [isEditingMethods, setIsEditingMethods] = useState(false);
  const [controlSortKey, setControlSortKey] = useState<string | null>(null);
  const [controlSortDir, setControlSortDir] = useState<"asc" | "desc">("asc");
  const [reviewSortKey, setReviewSortKey] = useState<string | null>(null);
  const [reviewSortDir, setReviewSortDir] = useState<"asc" | "desc">("asc");
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewPageSize, setReviewPageSize] = useState(10);
  const [reviewSearch, setReviewSearch] = useState("");

  const handleControlSort = (key: string) => {
    if (controlSortKey === key)
      setControlSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setControlSortKey(key);
      setControlSortDir("asc");
    }
    setControlPage(0);
  };

  /* ── Step 2: Assignment & Schedule ── */
  const [apiUsers, setApiUsers] = useState<ApiUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ApiUser[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [rulesOfEngagement, setRulesOfEngagement] = useState("");
  // const [assignTab, setAssignTab] = useState<0 | 1>(0);
  const [selectedAssessors, setSelectedAssessors] = useState<ApiUser[]>([]);
  const [assessorSearch, setAssessorSearch] = useState("");
  const [assetSearch, setAssetSearch] = useState("");

  /* ── Fetch SSPs ── */
  useEffect(() => {
    let cancelled = false;
    setSSPsLoading(true);
    listSSPs()
      .then((res) => {
        if (cancelled) return;
        let summaries = (res?.data ?? []).filter(
          (s) => s.lifecycleStatus === "published",
        );
        if (orgId) {
          summaries = summaries.filter((s) => s.owningOrgUnitId === orgId);
        }
        setApiSSPs(summaries.map((s) => apiSspToLocalSSP(s)));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load SSPs:", err);
        setApiSSPs([]);
      })
      .finally(() => {
        if (!cancelled) setSSPsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Fetch Users lazily when assignment step is reached ── */
  const usersLoadedRef = useRef(false);
  const [usersLoading, setUsersLoading] = useState(false);
  useEffect(() => {
    if (step < 2 || usersLoadedRef.current) return;
    usersLoadedRef.current = true;
    setUsersLoading(true);
    const selectedSSPObj = apiSSPs.find((s) => s.id === selectedSSP);
    listUsers(selectedSSPObj?.owningOrgUnitId, "system_owner")
      .then((res) => setApiUsers(res.data ?? []))
      .catch(() => setApiUsers([]))
      .finally(() => setUsersLoading(false));
  }, [step, selectedSSP, apiSSPs]);

  /* ── Load existing draft plan if editing ── */
  useEffect(() => {
    if (!editPlanId) return;
    let cancelled = false;
    setDraftLoading(true);

    (async () => {
      try {
        const [planRes, usersRes] = await Promise.all([
          getAssessmentPlan(editPlanId),
          listUsers().catch(() => ({ data: [] as ApiUser[] })),
        ]);
        if (cancelled) return;

        const plan = planRes.data;
        const allUsers = usersRes.data ?? [];

        // Pre-populate Step 0: Plan name & SSP
        if (plan.title) setPlanName(plan.title);
        let sspId: string | null = null;
        if (plan.importSspHref) {
          sspId = plan.importSspHref.split("/").pop() ?? plan.importSspHref;
          setSelectedSSP(sspId);
        }

        // Pre-populate Step 1: Reviewed controls
        const hasReviewedControls = !!(
          plan.reviewedControls && plan.reviewedControls.length > 0
        );
        if (hasReviewedControls) {
          const reviewedControlIds = new Set(
            plan.reviewedControls!.map((rc) => rc.controlId),
          );
          pendingReviewedControls.current = plan.reviewedControls!;
          pendingReviewedControlIds.current = reviewedControlIds;
        }

        // Pre-populate Step 2: Assignees & Schedule
        const hasSchedule = !!(plan.tasks && plan.tasks.length > 0);
        if (hasSchedule) {
          const task = plan.tasks![0];
          if (task.startDate) setScheduleStart(task.startDate.split("T")[0]);
          if (task.endDate) setScheduleEnd(task.endDate.split("T")[0]);

          const assigneeUserIds = new Set(
            plan.tasks!.flatMap((t) =>
              (t.assignees ?? []).map((a) => a.userId),
            ),
          );
          const matchedUsers = allUsers.filter((u) =>
            assigneeUserIds.has(u.id),
          );
          if (matchedUsers.length > 0) setSelectedUsers(matchedUsers);
        }

        // Resume at the furthest step that still needs work
        let resumeStep = 0;
        if (hasSchedule) resumeStep = 3;
        else if (hasReviewedControls) resumeStep = 2;
        else if (sspId && plan.title) resumeStep = 1;

        // If resuming past step 0, fetch the controls for the SSP
        if (resumeStep >= 1 && sspId) {
          await fetchControlsForSSP(sspId);
        }
        setStep(resumeStep);
      } catch (err) {
        console.error("Failed to load draft plan:", err);
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editPlanId]);

  /* ── Refs for draft plan pre-population ── */
  const pendingReviewedControls = useRef<any[] | null>(null);
  const pendingReviewedControlIds = useRef<Set<string> | null>(null);

  /* ── Apply reviewed controls from draft plan to loaded controls ── */
  const applyPendingReviewedControls = (loadedControls: WizardControl[]) => {
    if (!pendingReviewedControlIds.current || !pendingReviewedControls.current)
      return;

    const reviewedMap = new Map(
      pendingReviewedControls.current.map((rc: any) => [rc.controlId, rc]),
    );

    // Apply assessment methods to statements (locks those controls visually)
    // but do NOT pre-select — user starts fresh with "Select Assessment Method" count = 0
    for (const ctrl of loadedControls) {
      if (reviewedMap.has(ctrl.controlId)) {
        const rc = reviewedMap.get(ctrl.controlId);
        if (rc?.statements) {
          for (const rcStmt of rc.statements) {
            const localStmt = ctrl.statements.find(
              (s) => s.label === rcStmt.statementId,
            );
            if (localStmt && rcStmt.assessmentMethods) {
              for (const m of rcStmt.assessmentMethods) {
                localStmt.methods.add(m as AssessmentMethodType);
              }
            }
          }
        }
      }
    }

    // Clear pending data so it doesn't re-apply
    pendingReviewedControls.current = null;
    pendingReviewedControlIds.current = null;
  };

  /* ── Fetch Controls for the selected SSP (called on Continue from step 0) ── */
  const fetchControlsForSSP = async (sspId: string, dateParams?: { startDate?: string; endDate?: string }) => {
    setControlsLoading(true);
    setControls([]);
    setSelectedControlIds(new Set());
    setExpandedFamilies(new Set());
    setExpandedControls(new Set());

    try {
      const res = await listBaselineControls(sspId, dateParams);
      const rawData = res?.data as any;
      const baselineList: BaselineControlResponse[] =
        Array.isArray(rawData) ? rawData : (rawData?.controls ?? []);

      const allControls: WizardControl[] = baselineList.flatMap((bc) => {
        const controlTitle =
          bc.controlTitle ||
          bc.controlDetails?.title ||
          bc.title ||
          bc.controlId;
        const statementSeeds = extractStatementSeedsFromParts(
          bc.controlDetails?.parts,
        );

        // Controls with implemented requirements - one entry per requirement
        if (
          bc.implementedRequirements &&
          bc.implementedRequirements.length > 0
        ) {
          return bc.implementedRequirements.map((ir) => ({
            id: ir.id,
            controlId: bc.controlId,
            title: controlTitle,
            description: controlTitle,
            family: getControlFamily(bc.controlId),
            implementationStatus:
              ir.implementationStatus || bc.implementationStatus || "planned",
            origination: ir.origination ?? null,
            setParameters: [],
            byComponents: (ir.byComponents ??
              []) as unknown as ByComponentResponse[],
            statements: statementSeeds.map((statement) => ({
              ...statement,
              methods: new Set<AssessmentMethodType>(),
            })),
          }));
        }
        // Controls without implemented requirements - still show them
        return [
          {
            id: bc.controlId,
            controlId: bc.controlId,
            title: controlTitle,
            description: controlTitle,
            family: getControlFamily(bc.controlId),
            implementationStatus: bc.implementationStatus || "not-implemented",
            origination: null,
            setParameters: [],
            byComponents: [] as unknown as ByComponentResponse[],
            statements: statementSeeds.map((statement) => ({
              ...statement,
              methods: new Set<AssessmentMethodType>(),
            })),
          },
        ];
      });

      allControls.sort((a, b) =>
        a.controlId.localeCompare(b.controlId, undefined, {
          numeric: true,
        }),
      );
      setControls(allControls);
      applyPendingReviewedControls(allControls);
    } catch (err) {
      console.error("Failed to load controls:", err);
      setControls([]);
    } finally {
      setControlsLoading(false);
    }
  };

  /* ── Derived ── */
  const selectedSSPObj = apiSSPs.find((s) => s.id === selectedSSP);

  const filteredControls = useMemo(() => {
    let result = controls;
    // Status filter
    if (statusFilter !== "all") {
      result = result.filter(
        (c) => c.implementationStatus?.toLowerCase() === statusFilter,
      );
    }
    // Search filter
    const q = controlSearch.toLowerCase();
    if (q) {
      result = result.filter(
        (c) =>
          c.controlId.toLowerCase().includes(q) ||
          c.title.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q),
      );
    }
    return result;
  }, [controls, controlSearch, statusFilter]);

  // const availableUsers = apiUsers.filter((u: ApiUser) => !selectedUsers.some(su => su.id === u.id));
  // const filteredUsers = availableUsers.filter((u: ApiUser) =>
  //   u.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
  //   u.email.toLowerCase().includes(userSearch.toLowerCase())
  // );

  /* ── Control helpers ── */
  const toggleControl = (id: string) => {
    setSelectedControlIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setExpandedControls((p) => {
          const n = new Set(p);
          n.delete(id);
          return n;
        });
      } else next.add(id);
      return next;
    });
  };

  // const toggleFamily = (family: string) => {
  //   setExpandedFamilies(prev => {
  //     const next = new Set(prev);
  //     if (next.has(family)) next.delete(family); else next.add(family);
  //     return next;
  //   });
  // };

  const toggleControlExpand = (id: string) => {
    setExpandedControls((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyMethodToControls = (
    controlIds: string[],
    method: AssessmentMethodType,
  ) => {
    const updated = controls.map((ctrl) => {
      if (!controlIds.includes(ctrl.id)) return ctrl;
      return {
        ...ctrl,
        statements: ctrl.statements.map((stmt) => ({
          ...stmt,
          methods: new Set([method]),
        })),
      };
    });
    setControls(updated);
  };

  /* ── Navigation ── */
  const validatePlanName = (): boolean => {
    const requiredVal = validateRequired(planName, "Plan name");
    if (!requiredVal.valid) {
      setPlanNameError(requiredVal.error || "");
      return false;
    }

    const lengthVal = validateMaxLength(planName.trim(), 255, "Plan name");
    if (!lengthVal.valid) {
      setPlanNameError(lengthVal.error || "");
      return false;
    }

    setPlanNameError("");
    return true;
  };

  const isPlanNameValid = (): boolean => {
    const trimmed = planName.trim();
    return trimmed.length > 0 && trimmed.length <= 255;
  };

  const canNext = () => {
    if (step === 0) {
      return isPlanNameValid() && !!selectedSSP;
    }
    if (step === 1) {
      const hasLockedControls = controls.some((c) =>
        c.statements.some((s) => s.methods.size > 0),
      );
      return selectedControlIds.size > 0 || hasLockedControls;
    }
    if (step === 2)
      return selectedUsers.length > 0 && !!scheduleStart && !!scheduleEnd;
    return true;
  };

  const buildReviewedControls = () =>
    Array.from(selectedControlIds)
      .map((cId) => controls.find((c) => c.id === cId))
      .filter((ctrl): ctrl is WizardControl => !!ctrl)
      .map((ctrl) => ({
        controlId: ctrl.controlId,
        controlDescription: ctrl.title || ctrl.description || ctrl.controlId,
        implementationStatus: ctrl.implementationStatus || "",
        componentName: ctrl.byComponents?.[0]?.componentName || "",
        statements: ctrl.statements.map((s) => ({
          statementId: s.label,
          statementDescription: s.text,
          assessmentMethods: Array.from(s.methods),
        })),
      }));

  const handleNext = async () => {
    if (step === 0 && !validatePlanName()) return;
    if (!canNext()) return;
    setStepping(true);
    setStepError(null);

    try {
      // Step 0 → 1: Create draft plan + fetch controls
      if (step === 0 && selectedSSP) {
        if (!draftPlanIdRef.current) {
          const selectedSSPObj = apiSSPs.find((s) => s.id === selectedSSP);
          const res = await createAssessmentPlan(
            planName,
            `/api/v1/ssps/${selectedSSP}`,
            selectedSSPObj?.owningOrgUnitId,
          );
          draftPlanIdRef.current = res.data.id;
          setDraftPlanId(res.data.id);
        }
        await fetchControlsForSSP(selectedSSP);
      }

      // Step 1 → 2: Save reviewed controls
      if (step === 1 && draftPlanIdRef.current) {
        // Merge locked controls (already have methods) into selection before building payload
        const lockedIds = controls
          .filter((c) => c.statements.some((s) => s.methods.size > 0))
          .map((c) => c.id);
        const allSelectedIds = new Set([...selectedControlIds, ...lockedIds]);
        setSelectedControlIds(allSelectedIds);

        const reviewedPayload = Array.from(allSelectedIds)
          .map((cId) => controls.find((c) => c.id === cId))
          .filter((ctrl): ctrl is WizardControl => !!ctrl)
          .map((ctrl) => ({
            controlId: ctrl.controlId,
            controlDescription:
              ctrl.title || ctrl.description || ctrl.controlId,
            implementationStatus: ctrl.implementationStatus || "",
            componentName: ctrl.byComponents?.[0]?.componentName || "",
            statements: ctrl.statements.map((s) => ({
              statementId: s.label,
              statementDescription: s.text,
              assessmentMethods: Array.from(s.methods),
            })),
          }));
        await saveReviewedControls(draftPlanIdRef.current, reviewedPayload);
        // Keep selectedControlIds so the review step can display all selected controls
      }

      // Step 2 → 3: Save schedule & assignees
      if (step === 2 && draftPlanIdRef.current) {
        await saveAssessmentSchedule(draftPlanIdRef.current, {
          assigneeUserIds: selectedUsers.map((u) => u.id),
          startDate: new Date(scheduleStart).toISOString(),
          endDate: new Date(scheduleEnd).toISOString(),
        });
      }

      setStep((s) => s + 1);
    } catch (err: any) {
      const apiMsg =
        err?.responseBody?.errors?.[0]?.detail ||
        err?.responseBody?.errors?.[0]?.title ||
        err?.message;
      useToastStore
        .getState()
        .showError("Error", apiMsg || "Something went wrong");
    } finally {
      setStepping(false);
    }
  };

  /* Submit the plan (Review step) — DRAFT → IN_REVIEW */
  const handleSavePlan = async () => {
    if (!draftPlanIdRef.current) return;
    setStepping(true);
    setStepError(null);
    try {
      const selectedSSPObj = apiSSPs.find((s) => s.id === selectedSSP);
      await submitAssessmentPlan(
        draftPlanIdRef.current,
        selectedSSPObj?.owningOrgUnitId,
      );
      navigate("/assessment-plans");
    } catch (err: any) {
      const apiMsg =
        err?.responseBody?.errors?.[0]?.detail ||
        err?.responseBody?.errors?.[0]?.title ||
        err?.message;
      useToastStore
        .getState()
        .showError("Error", apiMsg || "Failed to submit assessment plan");
    } finally {
      setStepping(false);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep((s) => s - 1);
      setStepError(null);
    }
  };

  if (draftLoading) {
    return (
      <PageShell className="flex items-center justify-center gap-3 py-24">
        <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
        <span className="text-[13px] d">Loading draft plan</span>
      </PageShell>
    );
  }

  return (
    <PageShell className="space-y-6">
      <Breadcrumb parent="Assessment Plans" parentTo="/assessment-plans" />

      {/* Stepper */}
      <div className="px-8">
        <WorkflowStepper steps={WIZARD_STEPS} currentStep={step} />
      </div>

      {/* Step Content */}
      <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-6 min-h-[400px]">
        {/* ═══════════ Step 0: Select SSP ═══════════ */}
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-[#0f172a] dark:text-white">
                Select System Security Plan & Define Assessment
              </h2>
              <p className="text-sm d mt-1">
                Select an existing SSP that defines the scope of the Assessment
              </p>
            </div>

            {/* Plan Name */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#0f172a] dark:text-white">
                Assessment Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Q1 2026 Cloud Security Assessment"
                value={planName}
                onChange={(e) => {
                  setPlanName(e.target.value);
                  if (planNameError) setPlanNameError("");
                }}
                maxLength={255}
                className={`w-full max-w-md px-3 py-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white ${
                  planNameError
                    ? "border-red-500 dark:border-red-500"
                    : "border-[#e2e8f0] dark:border-white/[0.07]"
                }`}
              />
              {planNameError && (
                <span className="text-xs text-red-500">{planNameError}</span>
              )}
            </div>

            {/* SSP Label + Search */}
            <div className="space-y-3">
              <div className="flex items-center justify-between overflow-hidden pr-2 [scrollbar-gutter:stable]">
                <label className="text-sm font-medium text-[#0f172a] dark:text-white">
                  System Security Plan (SSP){" "}
                  <span className="text-red-500">*</span>
                </label>
                {selectedSSP && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 ring-1 ring-indigo-200 dark:ring-indigo-500/20">
                    <Check className="size-3" /> 1 selected
                  </span>
                )}
              </div>

              {sspsLoading ? (
                <div className="flex items-center justify-center gap-3 py-12">
                  <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
                  <span className="text-sm d">
                    Loading system security plans
                  </span>
                </div>
              ) : apiSSPs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <div className="size-12 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center">
                    <Shield className="size-5 text-[#64748b] dark:text-white/50" />
                  </div>
                  <p className="text-sm d">No SSPs available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[380px] overflow-y-auto [scrollbar-gutter:stable]">
                  {apiSSPs.map((ssp) => {
                    const isSelected = selectedSSP === ssp.id;
                    return (
                      <button
                        key={ssp.id}
                        onClick={() => setSelectedSSP(isSelected ? null : ssp.id)}
                        className={`flex items-center gap-3 text-left px-4 py-3 rounded-xl border-2 transition-all group ${
                          isSelected
                            ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/[0.06]"
                            : "border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-indigo-300 dark:hover:border-indigo-500/30"
                        }`}
                      >
                        {/* Radio — LEFT aligned, matching other pages */}
                        <div
                          className={`size-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                            isSelected
                              ? "border-indigo-600 dark:border-indigo-500 bg-indigo-600 dark:bg-indigo-500"
                              : "border-[#cbd5e1] dark:border-white/25 group-hover:border-indigo-400 dark:group-hover:border-indigo-400"
                          }`}
                        >
                          {isSelected && <div className="size-2 rounded-full bg-white" />}
                        </div>
                        {/* Shield icon */}
                        <div
                          className={`size-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? "bg-indigo-100 dark:bg-indigo-500/15"
                              : "bg-[#f1f5f9] dark:bg-white/[0.08]"
                          }`}
                        >
                          <Shield
                            className={`size-4 ${
                              isSelected
                                ? "text-indigo-600 dark:text-indigo-400"
                                : "text-[#64748b] dark:text-white/50 group-hover:text-indigo-500 dark:group-hover:text-indigo-400"
                            }`}
                          />
                        </div>
                        {/* Title */}
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-semibold truncate ${
                              isSelected
                                ? "text-indigo-700 dark:text-indigo-300"
                                : "text-[#0f172a] dark:text-white"
                            }`}
                          >
                            {ssp.title}
                          </p>
                          {ssp.profileName && (
                            <p className="text-xs text-[#64748b] dark:text-white/40 mt-0.5 truncate">
                              Profile: {ssp.profileName}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ Step 1: Select Controls ═══════════ */}
        {step === 1 &&
          (() => {
            // Flatten controls → one row per statement (or one row if no statements)
            type ControlRow = {
              ctrl: WizardControl;
              stmt: WizardStatement | null;
            };
            const allRows: ControlRow[] = filteredControls.flatMap<ControlRow>(
              (ctrl) =>
                ctrl.statements.length > 0
                  ? ctrl.statements.map((stmt) => ({ ctrl, stmt }))
                  : [{ ctrl, stmt: null }],
            );

            // Sort
            const sortedRows = [...allRows].sort((a, b) => {
              if (!controlSortKey) return 0;
              let aVal = "",
                bVal = "";
              if (controlSortKey === "controlId") {
                aVal = a.ctrl.controlId;
                bVal = b.ctrl.controlId;
              } else if (controlSortKey === "title") {
                aVal = a.ctrl.description || a.ctrl.title;
                bVal = b.ctrl.description || b.ctrl.title;
              } else if (controlSortKey === "statement") {
                aVal = a.stmt?.text ?? "";
                bVal = b.stmt?.text ?? "";
              } else if (controlSortKey === "component") {
                aVal = a.ctrl.byComponents?.[0]?.componentName ?? "";
                bVal = b.ctrl.byComponents?.[0]?.componentName ?? "";
              } else if (controlSortKey === "status") {
                aVal = a.ctrl.implementationStatus ?? "";
                bVal = b.ctrl.implementationStatus ?? "";
              } else if (controlSortKey === "method") {
                aVal = Array.from(a.stmt?.methods ?? [])[0] ?? "";
                bVal = Array.from(b.stmt?.methods ?? [])[0] ?? "";
              }
              const cmp = aVal.localeCompare(bVal);
              return controlSortDir === "asc" ? cmp : -cmp;
            });

            const pagedRows = sortedRows.slice(
              controlPage * controlPageSize,
              (controlPage + 1) * controlPageSize,
            );

            const SortIcon = ({ col }: { col: string }) => {
              const active = controlSortKey === col;
              return (
                <span
                  className="inline-flex flex-col items-center ml-2 select-none"
                  style={{ fontSize: "7px", lineHeight: "7px" }}
                >
                  <span
                    className={`font-black ${active && controlSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
                  >
                    ▲
                  </span>
                  <span
                    className={`font-black ${active && controlSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
                  >
                    ▼
                  </span>
                </span>
              );
            };

            // Selection helpers
            const pageControlIds = [
              ...new Set(pagedRows.map((r) => r.ctrl.id)),
            ];
            const allPageSelected =
              pageControlIds.length > 0 &&
              pageControlIds.every((id) => selectedControlIds.has(id));
            const somePageSelected =
              pageControlIds.some((id) => selectedControlIds.has(id)) &&
              !allPageSelected;

            const toggleRow = (ctrlId: string) => {
              setSelectedControlIds((prev) => {
                const next = new Set(prev);
                if (next.has(ctrlId)) next.delete(ctrlId);
                else next.add(ctrlId);
                return next;
              });
            };

            const toggleAll = () => {
              setSelectedControlIds((prev) => {
                const next = new Set(prev);
                if (allPageSelected)
                  pageControlIds.forEach((id) => next.delete(id));
                else pageControlIds.forEach((id) => next.add(id));
                return next;
              });
            };

            const controlColumns: Column<ControlRow>[] = [
              {
                key: "no",
                header: (
                  <div className="flex items-center justify-center">
                    <SelectAllCheckbox
                      checked={allPageSelected}
                      indeterminate={somePageSelected}
                      onChange={toggleAll}
                    />
                  </div>
                ),
                align: "center",
                render: (row) => {
                  const hasMethod = row.ctrl.statements.some(
                    (s) => s.methods.size > 0,
                  );
                  const locked = hasMethod && !isEditingMethods;
                  return (
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={
                          locked ? true : selectedControlIds.has(row.ctrl.id)
                        }
                        onChange={() => {
                          if (!locked) toggleRow(row.ctrl.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={locked}
                        className={`w-3.5 h-3.5 rounded border-[#cbd5e1] dark:border-white/20 accent-indigo-600 ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}
                      />
                    </div>
                  );
                },
              },
              {
                key: "control",
                header: (
                  <button
                    onClick={() => handleControlSort("controlId")}
                    className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Control Id
                    <SortIcon col="controlId" />
                  </button>
                ),
                align: "center",
                render: (row) => (
                  <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded">
                    {row.ctrl.controlId?.toUpperCase()}
                  </span>
                ),
              },
              {
                key: "controlDesc",
                header: (
                  <button
                    onClick={() => handleControlSort("title")}
                    className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Title
                    <SortIcon col="title" />
                  </button>
                ),
                align: "left",
                headerClassName: "!text-center",
                ellipsis: 2,
                tooltip: (row) => row.ctrl.description || row.ctrl.title || "",
                render: (row) => (
                  <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">
                    {row.ctrl.description || row.ctrl.title}
                  </p>
                ),
              },
              {
                key: "stmtDesc",
                header: (
                  <button
                    onClick={() => handleControlSort("statement")}
                    className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Statement
                    <SortIcon col="statement" />
                  </button>
                ),
                align: "left",
                headerClassName: "!text-center",
                ellipsis: 2,
                tooltip: (row) => row.stmt?.text || "",
                render: (row) =>
                  row.stmt ? (
                    <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">
                      {row.stmt.text}
                    </p>
                  ) : (
                    <span className="text-[#94a3b8] dark:text-white/30">-</span>
                  ),
              },
              {
                key: "component",
                header: (
                  <button
                    onClick={() => handleControlSort("component")}
                    className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Component
                    <SortIcon col="component" />
                  </button>
                ),
                align: "left",
                render: (row) =>
                  row.ctrl.byComponents?.length > 0 ? (
                    <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80 break-words">
                      {row.ctrl.byComponents
                        .map((c) => c.componentName || c.componentUuid)
                        .join(", ")}
                    </span>
                  ) : (
                    <span className="text-[#94a3b8] dark:text-white/30">-</span>
                  ),
              },
              {
                key: "status",
                header: (
                  <button
                    onClick={() => handleControlSort("status")}
                    className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Status
                    <SortIcon col="status" />
                  </button>
                ),
                align: "center",
                render: (row) => {
                  const s = row.ctrl.implementationStatus?.toLowerCase();
                  const styles: Record<string, string> = {
                    implemented:
                      "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-200 dark:ring-emerald-500/20",
                    partial:
                      "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20",
                    planned:
                      "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-blue-200 dark:ring-blue-500/20",
                    "not-implemented":
                      "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 ring-red-200 dark:ring-red-500/20",
                  };
                  const labels: Record<string, string> = {
                    implemented: "Implemented",
                    partial: "Partial",
                    planned: "Planned",
                    "not-implemented": "Not Implemented",
                  };
                  if (!s || !styles[s])
                    return (
                      <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">
                        -
                      </span>
                    );
                  return (
                    <span
                      className={`inline-flex items-center justify-center w-[120px] py-0.5 rounded-full text-[11px] font-bold ring-1 whitespace-nowrap ${styles[s]}`}
                    >
                      {labels[s]}
                    </span>
                  );
                },
              },
              {
                key: "method",
                header: (
                  <button
                    onClick={() => handleControlSort("method")}
                    className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Assessment Method
                    <SortIcon col="method" />
                  </button>
                ),
                align: "center",
                render: (row) => {
                  const method = row.stmt
                    ? Array.from(row.stmt.methods)[0]
                    : undefined;
                  if (!method)
                    return (
                      <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">
                        Not selected
                      </span>
                    );
                  const styles: Record<string, string> = {
                    interview:
                      "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 ring-sky-200 dark:ring-sky-500/20",
                    examine:
                      "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 ring-indigo-200 dark:ring-indigo-500/20",
                    test: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20",
                  };
                  return (
                    <span
                      className={`inline-flex items-center justify-center w-[80px] py-0.5 rounded-full text-[11px] font-bold ring-1 capitalize ${styles[method] ?? styles.test}`}
                    >
                      {method}
                    </span>
                  );
                },
              },
            ];

            return (
              <div className="space-y-4">
                {/* Title row */}
                <div>
                  <h2 className="text-base font-semibold text-[#0f172a] dark:text-white">
                    Select & Configure Controls
                  </h2>
                  <p className="text-sm d mt-0.5">
                    Select Controls and Configure Assessment methods for each
                    Statement.
                  </p>
                </div>

                {/* Toolbar row */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Search */}
                  <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
                    <input
                      type="text"
                      placeholder="Search controls"
                      value={controlSearch}
                      onChange={(e) => {
                        setControlSearch(e.target.value);
                        setControlPage(0);
                      }}
                      className="w-full pl-9 pr-4 py-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
                    />
                  </div>

                  {/* Status filter */}
                  <Select
                    value={statusFilter}
                    onValueChange={(val) => {
                      setStatusFilter(val);
                      setControlPage(0);
                    }}
                  >
                    <SelectTrigger
                      className="w-36 h-[38px] text-[12px] font-semibold flex-shrink-0"
                    >
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="implemented">Implemented</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="not-implemented">
                        Not Implemented
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Select Assessment Method */}
                  <button
                    onClick={() => {
                      if (selectedControlIds.size > 0) {
                        setModalMethod("");
                        setShowMethodModal(true);
                      }
                    }}
                    disabled={selectedControlIds.size === 0}
                    className="flex items-center gap-2 h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold transition-all shadow-sm shadow-indigo-500/20 flex-shrink-0"
                  >
                    <ListChecks className="size-3.5" />
                    Select Assessment Method
                    {selectedControlIds.size > 0 && (
                      <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/20 text-[11px] font-bold leading-none">
                        {selectedControlIds.size}
                      </span>
                    )}
                  </button>

                  {/* Edit Methods */}
                  {controls.some((c) =>
                    c.statements.some((s) => s.methods.size > 0),
                  ) && (
                    <button
                      onClick={() => {
                        if (isEditingMethods) {
                          const updated = controls.map((c) =>
                            c.statements.some((s) => s.methods.size > 0) &&
                            !selectedControlIds.has(c.id)
                              ? {
                                  ...c,
                                  statements: c.statements.map((s) => ({
                                    ...s,
                                    methods: new Set<AssessmentMethodType>(),
                                  })),
                                }
                              : c,
                          );
                          setControls(updated);
                          setIsEditingMethods(false);
                          setSelectedControlIds(new Set());
                        } else {
                          setIsEditingMethods(true);
                          setSelectedControlIds(
                            new Set(
                              controls
                                .filter((c) =>
                                  c.statements.some((s) => s.methods.size > 0),
                                )
                                .map((c) => c.id),
                            ),
                          );
                        }
                      }}
                      className={`flex items-center gap-2 h-9 px-4 rounded-lg border text-[13px] font-semibold transition-all flex-shrink-0 ${
                        isEditingMethods
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
                          : "border-[#e2e8f0] dark:border-white/[0.1] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06]"
                      }`}
                    >
                      <Pencil className="size-3.5" />
                      {isEditingMethods ? "Done Editing" : "Edit Methods"}
                    </button>
                  )}

                  {/* Rows per page — pushed to far right */}
                  <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                    <label className="text-sm d whitespace-nowrap">
                      Rows per page
                    </label>
                    <Dropdown
                      value={String(controlPageSize)}
                      onChange={(v) => {
                        setControlPageSize(Number(v));
                        setControlPage(0);
                      }}
                      options={[10, 25, 50, 100].map((n) => ({
                        value: String(n),
                        label: String(n),
                      }))}
                      size="sm"
                      align="end"
                      className="!min-w-[80px]"
                    />
                  </div>
                </div>

                <DataTable<ControlRow>
                  columns={controlColumns}
                  data={pagedRows}
                  loading={controlsLoading}
                  emptyText="No controls found for this SSP"
                  rowKey={(row, idx) => `${row.ctrl.id}-${row.stmt?.id ?? idx}`}
                  page={controlPage}
                  pageSize={controlPageSize}
                  totalItems={sortedRows.length}
                  onPageChange={setControlPage}
                  itemLabel="controls"
                  compact
                  rowClassName={(row) =>
                    selectedControlIds.has(row.ctrl.id)
                      ? "bg-indigo-50/40 dark:bg-indigo-500/[0.06]"
                      : ""
                  }
                />
              </div>
            );
          })()}

        {/* ═══════════ Step 2: Assignment & Schedule ═══════════ */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-[#0f172a] dark:text-white">
                Assign Owners & Schedule
              </h2>
              <p className="text-sm d mt-1">
                Assign , System Owners, select Assessment Assets,
                and define the Schedule.
              </p>
            </div>

            {/* ── Tab Bar ── */}
            {/* <div className="flex gap-0 border-b border-[#e2e8f0] dark:border-white/[0.07]">
              {[
                { label: "Assessor Owners", icon: Shield, count: selectedAssessors.length },
                { label: "System Owners", icon: User, count: selectedUsers.length },
              ].map((tab, i) => {
                const active = assignTab === i;
                return (
                  <button
                    key={tab.label}
                    onClick={() => setAssignTab(i as 0 | 1)}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px ${
                      active
                        ? "border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400"
                        : "border-transparent text-[#64748b] dark:text-white/50 hover:text-[#475569] dark:hover:text-white/50"
                    }`}
                  >
                    <tab.icon className="size-4" />
                    {tab.label}
                    {tab.count > 0 && (
                      <span className="ml-1 text-[10px] font-bold bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded-full">
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div> */}

            {/* ═══ Assessor Owners + Assessment Assets (commented out) ═══ */}
            {
              /* assignTab === 0 */ false && (
                <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
                  {/* Card: Assessor Owners */}
                  <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.01] overflow-hidden flex flex-col">
                    <div className="px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] flex items-center gap-2">
                      <Shield className="size-4 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-sm font-semibold text-[#0f172a] dark:text-white">
                        Assessor Owners
                      </h3>
                      {selectedAssessors.length > 0 && (
                        <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-600 dark:bg-indigo-600 text-white">
                          {selectedAssessors.length} selected
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
                        <input
                          type="text"
                          placeholder="Search by Name or Email"
                          value={assessorSearch}
                          onChange={(e) => setAssessorSearch(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="px-3 pb-3 space-y-1.5 h-[300px] overflow-y-auto">
                      {apiUsers
                        .filter((u) => {
                          const q = assessorSearch.toLowerCase();
                          if (!q) return true;
                          return (
                            u.displayName.toLowerCase().includes(q) ||
                            u.email.toLowerCase().includes(q)
                          );
                        })
                        .map((u) => {
                          const isChecked = selectedAssessors.some(
                            (su) => su.id === u.id,
                          );
                          return (
                            <button
                              key={u.id}
                              onClick={() => {
                                if (isChecked)
                                  setSelectedAssessors((prev) =>
                                    prev.filter((su) => su.id !== u.id),
                                  );
                                else
                                  setSelectedAssessors((prev) => [...prev, u]);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                                isChecked
                                  ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/[0.06]"
                                  : "border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-indigo-300 dark:hover:border-indigo-500/30"
                              }`}
                            >
                              <div
                                className={`size-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${isChecked ? "bg-indigo-600 dark:bg-indigo-600 border-indigo-600 dark:border-indigo-600" : "border-[#cbd5e1] dark:border-white/20"}`}
                              >
                                {isChecked && (
                                  <Check className="size-3 text-white" />
                                )}
                              </div>
                              <div className="size-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-700 dark:text-indigo-300 flex-shrink-0">
                                {getInitials(u.displayName)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#0f172a] dark:text-white">
                                  {u.displayName}
                                </p>
                                <p className="text-[11px] d">{u.email}</p>
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  {/* Card: Assessment Assets */}
                  <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.01] overflow-hidden flex flex-col">
                    <div className="px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] flex items-center gap-2">
                      <Wrench className="size-4 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-sm font-semibold text-[#0f172a] dark:text-white">
                        Assessment Assets
                      </h3>
                      {selectedTools.size > 0 && (
                        <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-600 dark:bg-indigo-600 text-white">
                          {selectedTools.size} selected
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
                        <input
                          type="text"
                          placeholder="Search assets"
                          value={assetSearch}
                          onChange={(e) => setAssetSearch(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="px-3 pb-3 space-y-1.5 h-[300px] overflow-y-auto">
                      {[
                        "Nessus",
                        "STIG Viewer",
                        "OpenSCAP",
                        "Burp Suite",
                        "Qualys",
                        "Splunk",
                      ]
                        .filter(
                          (tool) =>
                            !assetSearch ||
                            tool
                              .toLowerCase()
                              .includes(assetSearch.toLowerCase()),
                        )
                        .map((tool) => {
                          const isSelected = selectedTools.has(tool);
                          return (
                            <button
                              key={tool}
                              onClick={() => {
                                setSelectedTools((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(tool)) next.delete(tool);
                                  else next.add(tool);
                                  return next;
                                });
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                                isSelected
                                  ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/[0.06]"
                                  : "border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-indigo-300 dark:hover:border-indigo-500/30"
                              }`}
                            >
                              <div
                                className={`size-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? "bg-indigo-600 dark:bg-indigo-600 border-indigo-600 dark:border-indigo-600" : "border-[#cbd5e1] dark:border-white/20"}`}
                              >
                                {isSelected && (
                                  <Check className="size-3 text-white" />
                                )}
                              </div>
                              <Server className="size-4 text-slate-500 dark:text-white/40 flex-shrink-0" />
                              <span className="text-sm font-medium text-[#0f172a] dark:text-white truncate">
                                {tool}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )
            }

            {/* ═══ System Owners + Schedule + Live Summary ═══ */}
            {true && (
              <div className="space-y-6">
                {/* Row: System Owners + Schedule */}
                <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
                  {/* Card: System Owners */}
                  <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.01] overflow-hidden flex flex-col">
                    <div className="px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] flex items-center gap-2">
                      <User className="size-4 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-sm font-semibold text-[#0f172a] dark:text-white">
                        System Owners
                      </h3>
                      {selectedUsers.length > 0 && (
                        <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-600 dark:bg-indigo-600 text-white">
                          {selectedUsers.length} selected
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
                        <input
                          type="text"
                          placeholder="Search by Name or Email"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="px-3 pb-3 space-y-1.5 h-[260px] overflow-y-auto">
                      {usersLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
                        </div>
                      ) : (
                        apiUsers
                          .filter((u) => {
                            const q = userSearch.toLowerCase();
                            if (!q) return true;
                            return (
                              u.displayName.toLowerCase().includes(q) ||
                              u.email.toLowerCase().includes(q)
                            );
                          })
                          .map((u) => {
                            const isChecked = selectedUsers.some(
                              (su) => su.id === u.id,
                            );
                            return (
                              <button
                                key={u.id}
                                onClick={() => {
                                  if (isChecked)
                                    setSelectedUsers((prev) =>
                                      prev.filter((su) => su.id !== u.id),
                                    );
                                  else setSelectedUsers((prev) => [...prev, u]);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                                  isChecked
                                    ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/[0.06]"
                                    : "border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-indigo-300 dark:hover:border-indigo-500/30"
                                }`}
                              >
                                <div
                                  className={`size-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${isChecked ? "bg-indigo-600 dark:bg-indigo-600 border-indigo-600 dark:border-indigo-600" : "border-[#cbd5e1] dark:border-white/20"}`}
                                >
                                  {isChecked && (
                                    <Check className="size-3 text-white" />
                                  )}
                                </div>
                                <div className="size-8 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-[10px] font-bold text-emerald-700 dark:text-emerald-300 flex-shrink-0">
                                  {getInitials(u.displayName)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#0f172a] dark:text-white">
                                    {u.displayName}
                                  </p>
                                  <p className="text-[11px] d">{u.email}</p>
                                </div>
                              </button>
                            );
                          })
                      )}
                    </div>
                  </div>

                  {/* Card: Assessment Schedule */}
                  <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.01] overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] flex items-center gap-2">
                      <Calendar className="size-4 text-indigo-600 dark:text-indigo-400" />
                      <h3 className="text-sm font-semibold text-[#0f172a] dark:text-white">
                        Assessment Schedule
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-[#475569] dark:text-white/50 tracking-wider">
                          Start Date
                        </label>
                        <div
                          className="relative cursor-pointer"
                          onClick={(e) => {
                            const input = (
                              e.currentTarget as HTMLElement
                            ).querySelector("input");
                            if (input) {
                              try {
                                input.showPicker();
                              } catch {
                                input.focus();
                              }
                            }
                          }}
                        >
                          <input
                            type="date"
                            value={scheduleStart}
                            onChange={(e) => setScheduleStart(e.target.value)}
                            className="w-full pl-3 pr-10 py-2.5 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all text-transparent dark:text-transparent cursor-pointer [&::-webkit-datetime-edit]:text-transparent [&::-webkit-datetime-edit-fields-wrapper]:text-transparent [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                          />
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none text-[#0f172a] dark:text-white bg-white dark:bg-[#1a1a2e] pr-2">
                            {scheduleStart
                              ? new Date(
                                  scheduleStart + "T00:00:00",
                                ).toLocaleDateString("en-GB")
                              : "DD/MM/YYYY"}
                          </span>
                          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 size-4 d pointer-events-none" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-[#475569] dark:text-white/50 tracking-wider">
                          End Date
                        </label>
                        <div
                          className="relative cursor-pointer"
                          onClick={(e) => {
                            const input = (
                              e.currentTarget as HTMLElement
                            ).querySelector("input");
                            if (input) {
                              try {
                                input.showPicker();
                              } catch {
                                input.focus();
                              }
                            }
                          }}
                        >
                          <input
                            type="date"
                            value={scheduleEnd}
                            onChange={(e) => setScheduleEnd(e.target.value)}
                            min={scheduleStart}
                            className="w-full pl-3 pr-10 py-2.5 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all text-transparent dark:text-transparent cursor-pointer [&::-webkit-datetime-edit]:text-transparent [&::-webkit-datetime-edit-fields-wrapper]:text-transparent [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                          />
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none text-[#0f172a] dark:text-white bg-white dark:bg-[#1a1a2e] pr-2">
                            {scheduleEnd
                              ? new Date(
                                  scheduleEnd + "T00:00:00",
                                ).toLocaleDateString("en-GB")
                              : "DD/MM/YYYY"}
                          </span>
                          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 size-4 d pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Summary - Review Snapshot (commented out for now) */}
                {/* <div className="rounded-xl border border-indigo-200 dark:border-indigo-500/20 bg-indigo-50/50 dark:bg-indigo-500/[0.04] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="size-6 rounded-md bg-indigo-600 dark:bg-indigo-600 flex items-center justify-center">
                      <Check className="size-3.5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-[#0f172a] dark:text-white">Review Snapshot</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-black tracking-wide text-indigo-500/70 dark:text-indigo-400/60">Assessor Owners</p>
                      {selectedAssessors.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {selectedAssessors.map((u) => (
                            <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-white/[0.06] text-[11px] font-medium text-[#0f172a] dark:text-white/80 border border-[#e2e8f0] dark:border-white/[0.08]">
                              <div className="size-4 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-[7px] font-bold text-indigo-700 dark:text-indigo-300">
                                {getInitials(u.displayName)}
                              </div>
                              {u.displayName}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] d">None</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-black tracking-wide text-indigo-500/70 dark:text-indigo-400/60">Assets</p>
                      {selectedTools.size > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {[...selectedTools].map((tool) => (
                            <span key={tool} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-white/[0.06] text-[11px] font-medium text-[#0f172a] dark:text-white/80 border border-[#e2e8f0] dark:border-white/[0.08]">
                              <Server className="size-3 text-slate-400" />
                              {tool}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] d">None</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-black tracking-wide text-indigo-500/70 dark:text-indigo-400/60">System Owners</p>
                      {selectedUsers.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {selectedUsers.map((u) => (
                            <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-white/[0.06] text-[11px] font-medium text-[#0f172a] dark:text-white/80 border border-[#e2e8f0] dark:border-white/[0.08]">
                              <div className="size-4 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-[7px] font-bold text-emerald-700 dark:text-emerald-300">
                                {getInitials(u.displayName)}
                              </div>
                              {u.displayName}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] d">None</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-black tracking-wide text-indigo-500/70 dark:text-indigo-400/60">Schedule</p>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="size-3 text-indigo-500 dark:text-indigo-400" />
                          <span className="text-[11px] text-[#475569] dark:text-white/50">Start:</span>
                          <span className="text-[11px] font-medium text-[#0f172a] dark:text-white">
                            {scheduleStart ? new Date(scheduleStart + "T00:00:00").toLocaleDateString("en-GB") : "Not set"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="size-3 text-indigo-500 dark:text-indigo-400" />
                          <span className="text-[11px] text-[#475569] dark:text-white/50">End:</span>
                          <span className="text-[11px] font-medium text-[#0f172a] dark:text-white">
                            {scheduleEnd ? new Date(scheduleEnd + "T00:00:00").toLocaleDateString("en-GB") : "Not set"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>
            )}
          </div>
        )}

        {/* ═══════════ Step 3: Review ═══════════ */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-semibold text-[#0f172a] dark:text-white">
                Review Assessment Plan
              </h2>
              <p className="text-sm d mt-1">
                Review all details before creating the assessment plan
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Plan Name */}
              <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] p-4 space-y-1.5">
                <p className="text-xs font-semibold text-[#0f172a] dark:text-white/70">
                  Assessment Plan Name
                </p>
                <p className="text-sm text-[#0f172a] dark:text-white">
                  {planName}
                </p>
              </div>

              {/* SSP */}
              <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] p-4 space-y-1.5">
                <p className="text-xs font-semibold text-[#0f172a] dark:text-white/70">
                  Selected SSP
                </p>
                <p className="text-sm text-[#0f172a] dark:text-white">
                  {selectedSSPObj?.name || "-"}
                </p>
              </div>

              {/* Controls — show freshly-selected + already-locked (saved) controls */}
              {(() => {
                const effectiveControls = controls.filter(
                  (c) =>
                    selectedControlIds.has(c.id) ||
                    c.statements.some((s) => s.methods.size > 0),
                );
                const METHOD_STYLES: Record<AssessmentMethodType, string> = {
                  interview: "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 ring-sky-200 dark:ring-sky-500/20",
                  examine: "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 ring-indigo-200 dark:ring-indigo-500/20",
                  test: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20",
                };
                const STATUS_STYLES: Record<string, string> = {
                  implemented: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-200 dark:ring-emerald-500/20",
                  partial: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20",
                  planned: "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-blue-200 dark:ring-blue-500/20",
                  "not-implemented": "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 ring-red-200 dark:ring-red-500/20",
                };
                const STATUS_LABELS: Record<string, string> = {
                  implemented: "Implemented",
                  partial: "Partial",
                  planned: "Planned",
                  "not-implemented": "Not Implemented",
                };

                // Flatten: one row per statement (or one row if no statements)
                type ReviewRow = { ctrl: WizardControl; stmt: WizardStatement | null };
                const allReviewRows: ReviewRow[] = effectiveControls.flatMap((ctrl) =>
                  ctrl.statements.length > 0
                    ? ctrl.statements.map((stmt) => ({ ctrl, stmt }))
                    : [{ ctrl, stmt: null }],
                );

                // Sort
                const handleReviewSort = (key: string) => {
                  if (reviewSortKey === key)
                    setReviewSortDir((d) => (d === "asc" ? "desc" : "asc"));
                  else {
                    setReviewSortKey(key);
                    setReviewSortDir("asc");
                  }
                };
                const ReviewSortIcon = ({ col }: { col: string }) => {
                  const active = reviewSortKey === col;
                  return (
                    <span className="inline-flex flex-col items-center ml-2 select-none" style={{ fontSize: "7px", lineHeight: "7px" }}>
                      <span className={`font-black ${active && reviewSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}>▲</span>
                      <span className={`font-black ${active && reviewSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}>▼</span>
                    </span>
                  );
                };
                const reviewRows = [...allReviewRows].sort((a, b) => {
                  if (!reviewSortKey) return 0;
                  let aVal = "", bVal = "";
                  if (reviewSortKey === "controlId") { aVal = a.ctrl.controlId; bVal = b.ctrl.controlId; }
                  else if (reviewSortKey === "title") { aVal = a.ctrl.description || a.ctrl.title; bVal = b.ctrl.description || b.ctrl.title; }
                  else if (reviewSortKey === "statement") { aVal = a.stmt?.text ?? ""; bVal = b.stmt?.text ?? ""; }
                  else if (reviewSortKey === "status") { aVal = a.ctrl.implementationStatus ?? ""; bVal = b.ctrl.implementationStatus ?? ""; }
                  else if (reviewSortKey === "method") { aVal = Array.from(a.stmt?.methods ?? [])[0] ?? ""; bVal = Array.from(b.stmt?.methods ?? [])[0] ?? ""; }
                  const cmp = aVal.localeCompare(bVal);
                  return reviewSortDir === "asc" ? cmp : -cmp;
                });

                // Search filter across controlId, title, statement text, status, methods
                const q = reviewSearch.trim().toLowerCase();
                const filteredReviewRows = q
                  ? reviewRows.filter((row) =>
                      row.ctrl.controlId.toLowerCase().includes(q) ||
                      (row.ctrl.description || row.ctrl.title).toLowerCase().includes(q) ||
                      (row.stmt?.text ?? "").toLowerCase().includes(q) ||
                      (row.ctrl.implementationStatus ?? "").toLowerCase().includes(q) ||
                      Array.from(row.stmt?.methods ?? []).some((m) => m.toLowerCase().includes(q))
                    )
                  : reviewRows;

                const pagedReviewRows = filteredReviewRows.slice(reviewPage * reviewPageSize, (reviewPage + 1) * reviewPageSize);

                return (
                  <div className="md:col-span-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <p className="text-xs font-semibold text-[#0f172a] dark:text-white/70 whitespace-nowrap">
                          Selected Controls ({filteredReviewRows.length}{q ? ` of ${effectiveControls.length}` : ""})
                        </p>
                        <div className="relative w-[220px]">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[#94a3b8] dark:text-white/30 pointer-events-none" />
                          <input
                            type="text"
                            placeholder="Search controls"
                            value={reviewSearch}
                            onChange={(e) => { setReviewSearch(e.target.value); setReviewPage(0); }}
                            className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#f8fafc] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-lg outline-none focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/30 text-[#0f172a] dark:text-white"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <label className="text-xs text-[#64748b] dark:text-white/50 whitespace-nowrap">Rows per page</label>
                        <Dropdown
                          value={String(reviewPageSize)}
                          onChange={(v) => { setReviewPageSize(Number(v)); setReviewPage(0); }}
                          options={[10, 25, 50].map((n) => ({ value: String(n), label: String(n) }))}
                          size="sm"
                          align="end"
                          className="!min-w-[72px]"
                        />
                      </div>
                    </div>
                    <DataTable<ReviewRow>
                      columns={[
                        {
                          key: "control",
                          header: (
                            <button onClick={() => handleReviewSort("controlId")} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              Control ID<ReviewSortIcon col="controlId" />
                            </button>
                          ),
                          align: "center",
                          render: (row) => (
                            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded">
                              {row.ctrl.controlId?.toUpperCase()}
                            </span>
                          ),
                        },
                        {
                          key: "title",
                          header: (
                            <button onClick={() => handleReviewSort("title")} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              Title<ReviewSortIcon col="title" />
                            </button>
                          ),
                          align: "left",
                          headerClassName: "!text-center",
                          render: (row) => (
                            <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">
                              {row.ctrl.description || row.ctrl.title}
                            </p>
                          ),
                        },
                        {
                          key: "statement",
                          header: (
                            <button onClick={() => handleReviewSort("statement")} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              Statement<ReviewSortIcon col="statement" />
                            </button>
                          ),
                          align: "left",
                          headerClassName: "!text-center",
                          render: (row) =>
                            row.stmt ? (
                              <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">
                                {row.stmt.text}
                              </p>
                            ) : (
                              <span className="text-[#94a3b8] dark:text-white/30">-</span>
                            ),
                        },
                        {
                          key: "status",
                          header: (
                            <button onClick={() => handleReviewSort("status")} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              Status<ReviewSortIcon col="status" />
                            </button>
                          ),
                          align: "center",
                          render: (row) => {
                            const s = row.ctrl.implementationStatus?.toLowerCase();
                            if (!s || !STATUS_STYLES[s])
                              return <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">-</span>;
                            return (
                              <span className={`inline-flex items-center justify-center w-[120px] py-0.5 rounded-full text-[11px] font-bold ring-1 whitespace-nowrap ${STATUS_STYLES[s]}`}>
                                {STATUS_LABELS[s]}
                              </span>
                            );
                          },
                        },
                        {
                          key: "methods",
                          header: (
                            <button onClick={() => handleReviewSort("method")} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                              Assessment Methods<ReviewSortIcon col="method" />
                            </button>
                          ),
                          align: "center",
                          render: (row) => {
                            const methods = row.stmt
                              ? row.stmt.methods
                              : (() => {
                                  const all = new Set<AssessmentMethodType>();
                                  row.ctrl.statements.forEach((s) => s.methods.forEach((m) => all.add(m)));
                                  return all;
                                })();
                            if (methods.size === 0)
                              return <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">Not selected</span>;
                            return (
                              <div className="flex items-center justify-center gap-1 flex-wrap">
                                {(["interview", "examine", "test"] as AssessmentMethodType[])
                                  .filter((m) => methods.has(m))
                                  .map((m) => (
                                    <span key={m} className={`inline-flex items-center justify-center w-[80px] py-0.5 rounded-full text-[11px] font-bold ring-1 capitalize ${METHOD_STYLES[m]}`}>
                                      {m}
                                    </span>
                                  ))}
                              </div>
                            );
                          },
                        },
                      ]}
                      data={pagedReviewRows}
                      rowKey={(row, idx) => `${row.ctrl.id}-${row.stmt?.id ?? idx}`}
                      emptyText="No controls selected"
                      compact
                      page={reviewPage}
                      pageSize={reviewPageSize}
                      totalItems={filteredReviewRows.length}
                      onPageChange={setReviewPage}
                      itemLabel="controls"
                    />
                  </div>
                );
              })()}

              {/* Assessors + Schedule — side by side */}
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] p-4 space-y-2">
                  <p className="text-xs font-semibold text-[#0f172a] dark:text-white/70">
                    System Owners
                  </p>
                  {selectedUsers.length > 0 ? (
                    <div className="space-y-2">
                      {selectedUsers.map((u) => (
                        <div key={u.id} className="flex items-center gap-2.5">
                          <div className="size-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-700 dark:text-indigo-300 flex-shrink-0">
                            {getInitials(u.displayName)}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-[#0f172a] dark:text-white">
                              {u.displayName}
                            </p>
                            <p className="text-[10px] d">{u.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs d">None assigned</p>
                  )}
                </div>

                <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] p-4 space-y-1.5">
                  <p className="text-xs font-semibold text-[#0f172a] dark:text-white/70">
                    Assessment Schedule
                  </p>
                  <div className="flex gap-8 text-sm">
                    <div>
                      <span className="text-[#475569] dark:text-white/50">Start:</span>{" "}
                      <span className="text-[#0f172a] dark:text-white font-medium">
                        {scheduleStart
                          ? new Date(scheduleStart + "T00:00:00").toLocaleDateString("en-GB")
                          : "Not set"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#475569] dark:text-white/50">End:</span>{" "}
                      <span className="text-[#0f172a] dark:text-white font-medium">
                        {scheduleEnd
                          ? new Date(scheduleEnd + "T00:00:00").toLocaleDateString("en-GB")
                          : "Not set"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        {step > 0 ? (
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back
          </button>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-3">
          {step === 0 && (
            <button
              onClick={() => {
                if (draftPlanIdRef.current) {
                  setShowCancelConfirm(true);
                } else {
                  navigate("/assessment-plans");
                }
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-indigo-500/40 dark:border-indigo-500/30 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
            >
              Cancel
            </button>
          )}
          {step === 3 ? (
            <button
              onClick={handleSavePlan}
              disabled={stepping}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {stepping ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              New Plan
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canNext() || stepping}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {stepping ? <Loader2 className="size-4 animate-spin" /> : null}
              Next
              <ArrowRight className="size-4" />
            </button>
          )}
        </div>
      </div>
      <Modal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Leave without finishing?"
        footer={
          <>
            <button
              onClick={() => setShowCancelConfirm(false)}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Keep Editing
            </button>
            <button
              onClick={() => navigate("/assessment-plans")}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
            >
              Leave
            </button>
          </>
        }
      >
        <p className="text-sm text-[#475569] dark:text-white/60 leading-relaxed">
          This assessment plan has been saved as a draft. You can continue it
          later from the Assessment Plans list.
        </p>
      </Modal>
      <Modal
        open={showMethodModal}
        onClose={() => setShowMethodModal(false)}
        title="Assign Assessment Method"
        description={`Applying to ${selectedControlIds.size} selected control${selectedControlIds.size !== 1 ? "s" : ""}`}
        footer={
          <>
            <button
              onClick={() => setShowMethodModal(false)}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!modalMethod) return;
                applyMethodToControls(
                  [...selectedControlIds],
                  modalMethod as AssessmentMethodType,
                );
                setShowMethodModal(false);
                setModalMethod("");
                setIsEditingMethods(false);
                setSelectedControlIds(new Set());
              }}
              disabled={!modalMethod}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-indigo-500/20"
            >
              <ListChecks className="size-3.5" />
              Apply to {selectedControlIds.size} Control
              {selectedControlIds.size !== 1 ? "s" : ""}
            </button>
          </>
        }
      >
        <div>
          <p className="text-[11px] font-semibold text-[#94a3b8] dark:text-white/40 tracking-wide mb-3">
            Selected Controls
          </p>
          <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
            {[...selectedControlIds].map((ctrlId) => {
              const ctrl = controls.find((c) => c.id === ctrlId);
              const currentMethod = ctrl?.statements.flatMap((s) => [
                ...s.methods,
              ])[0];
              const methodStyles: Record<string, string> = {
                interview:
                  "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400",
                examine:
                  "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400",
                test: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400",
              };
              return (
                <div
                  key={ctrlId}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.04]"
                >
                  <span className="text-[11px] font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded flex-shrink-0">
                    {ctrl?.controlId?.toUpperCase() ?? ctrlId.slice(0, 8)}
                  </span>
                  <span className="text-[12px] text-[#475569] dark:text-white/60 truncate flex-1">
                    {ctrl?.description || ctrl?.title || ctrlId?.toUpperCase()}
                  </span>
                  {currentMethod && (
                    <span
                      className={`text-[11px] font-semibold capitalize px-2 py-0.5 rounded-full flex-shrink-0 ${methodStyles[currentMethod] ?? ""}`}
                    >
                      {currentMethod}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-[#94a3b8] dark:text-white/40 tracking-wide mb-3">
            Assessment Method
          </p>
          <div className="grid grid-cols-3 gap-3">
            {(
              [
                { value: "interview", label: "Interview", color: "sky" },
                { value: "examine", label: "Examine", color: "indigo" },
                { value: "test", label: "Test", color: "amber" },
              ] as const
            ).map(({ value, label, color }) => {
              const isSelected = modalMethod === value;
              const colorMap = {
                sky: {
                  border: "border-sky-500",
                  bg: "bg-sky-50 dark:bg-sky-500/10",
                  dot: "bg-sky-500",
                  text: "text-sky-700 dark:text-sky-400",
                },
                indigo: {
                  border: "border-indigo-500",
                  bg: "bg-indigo-50 dark:bg-indigo-500/10",
                  dot: "bg-indigo-500",
                  text: "text-indigo-700 dark:text-indigo-400",
                },
                amber: {
                  border: "border-amber-500",
                  bg: "bg-amber-50 dark:bg-amber-500/10",
                  dot: "bg-amber-500",
                  text: "text-amber-700 dark:text-amber-400",
                },
              };
              const c = colorMap[color];
              return (
                <button
                  key={value}
                  onClick={() => setModalMethod(value)}
                  className={`flex items-center gap-2 px-4 py-3.5 rounded-xl border-2 transition-all ${
                    isSelected
                      ? `${c.border} ${c.bg}`
                      : "border-[#e2e8f0] dark:border-white/[0.06] hover:border-[#c7d2de] dark:hover:border-white/[0.12] bg-white dark:bg-transparent"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? c.dot : "bg-[#cbd5e1] dark:bg-white/20"}`}
                  />
                  <span
                    className={`text-[13px] font-semibold ${isSelected ? c.text : "text-[#475569] dark:text-white/60"}`}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>
    </PageShell>
  );
}
